import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { API } from "../constants";
import { useLang } from "../i18n";
import { useQuote } from "../quote";

const FALLBACK = [
  { slug: "olio-extra-vergine", name: "Olio Extra Vergine di Oliva", cat: "oil", unit: "100 ml", price: "€ 12", image: "/products/olio.jpg", description: { en: "Extra virgin olive oil from ancient olive trees — sealed in wax, with its certificate of origin." } },
  { slug: "vinvm-primitivvm-italicvm", name: "Vinvm Primitivvm Italicvm", cat: "redwine", unit: "750 ml", price: "€ 16", image: "/products/primitivo.jpg", description: { en: "Italian Primitivo, vintage MMXXVI — the Puglian sun, full and velvety, under the She-Wolf seal." } },
  { slug: "vinvm-primitivvm-rosatvm", name: "Vinvm Primitivvm Rosatvm", cat: "wine", unit: "750 ml", price: "€ 14", image: "/products/rose.jpg", description: { en: "A rosé from hand-picked primitivo grapes — the blush of a Roman evening, bottled." } },
  { slug: "vinvm-chardonnay", name: "Vinvm Chardonnay", cat: "white", unit: "750 ml", price: "€ 16", image: "/products/chardonnay.jpg", description: { en: "Vintage MMXXVI — pale gold from the hills of the emperors' toasts." } },
  { slug: "riserva-imperiale", name: "Riserva Imperiale", cat: "sparkling", unit: "750 ml", price: "€ 45", image: "/products/champagne.jpg", description: { en: "Metodo classico matured on the lees — the toast of emperors." } },
  { slug: "prosecco-doc", name: "Prosecco DOC", cat: "prosecco", unit: "750 ml", price: "€ 16", image: "/products/prosecco.jpg", description: { en: "Extra Dry prosecco — the everyday toast of the empire." } },
  { slug: "mel-carpathicum", name: "Mel Carpathicum", cat: "honey", unit: "500 g", price: "€ 15", image: "/products/honey.jpg", description: { en: "Raw high-mountain honey from the Carpathians — 100% pure." } },
  { slug: "energy-melograno", name: "Energy Drink al Melograno", cat: "energy", unit: "330 ml", price: "€ 4", image: "/products/energy.jpg", description: { en: "Pomegranate energy drink — the beverage of gladiators." } },
  { slug: "parmigiano-reggiano", name: "Parmigiano Reggiano DOP", cat: "cheese", unit: "Mesi XII–XLVIII", price: "€ 20", image: "/products/parmigiano.jpg", description: { en: "Aged from XII to XLVIII months — four ranks of patience." } },
  { slug: "parmigiano-classico", name: "Parmigiano Reggiano Classivs", cat: "cheese", unit: "XXIV Mesi · Confezione Regalo", price: "€ 30", image: "/products/parmigiano-classico.jpg", description: { en: "The Classivs XXIV-month edition in the Sapor Etrvscvs Antiqvvs box — wax seal, numbered certificate of authenticity and natural ageing." } },
  { slug: "kit-da-bar", name: "Kit da Bar con Dosatore", cat: "barkit", unit: "12 × 25 g + Dosatore", price: "€ 36", image: "/products/kitbar.jpg", description: { en: "Twelve 25 g honey mini-jars plus a professional no-drip dispenser." } },
  { slug: "prosciutto-crudo", name: "Prosciutto Crudo", cat: "salumi", unit: "Intero · 100% Italiano", price: "€ 60", image: "/products/prosciutto.jpg", description: { en: "Whole prosciutto crudo, 100% Italian — with the She-Wolf seal." } },
  { slug: "gin", name: "Gin", cat: "gin", unit: "700 ml", price: "€ 32", image: "/products/gin.jpg", description: { en: "Juniper clarity with a Roman heart, under the gaze of the Colosseum." } },
  { slug: "spumone-tricolore", name: "Spumone Tricolore", cat: "dolce", unit: "Porzione singola", price: "€ 8", image: "/products/spumone.jpg", description: { en: "Tricolor semifreddo crowned by a chocolate She-Wolf." } },
  { slug: "vodka", name: "Vodka", cat: "vodka", unit: "750 ml", price: "€ 28", image: "/products/vodka.jpg", description: { en: "A crystal-clear distillate crowned by the golden She-Wolf." } },
  { slug: "servizio-tavola", name: "Servizio da Tavola", cat: "ceramica", unit: "Servizio completo", price: "€ 120", image: "/products/servizio-tavola.jpg", description: { en: "Ceramic plates and coffee cups in imperial red with golden She-Wolf." } },
  { slug: "olio-latta", name: "Olio Extra Vergine in Latta", cat: "oil", unit: "100 ml", price: "€ 12", image: "/products/olio-latta.jpg", description: { en: "The extra virgin olive oil in its travel tin — 100 ml of Etruscan flavour." } },
  { slug: "panettone-artigianale", name: "Panettone Artigianale", cat: "panettone", unit: "100% Italiano", price: "€ 22", image: "/products/panettone.jpg", description: { en: "Artisan panettone, 100% Italian — the She-Wolf in gold on the wrap." } },
];

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.13, duration: 0.95, ease: [0.22, 1, 0.36, 1] } }),
};

function ProductCard({ p, i }) {
  const { t, lang } = useLang();
  const { add, openDrawer } = useQuote();

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const desc = p.description?.[lang] || p.description?.en;

  return (
    <motion.article
      variants={fadeUp}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      onMouseMove={onMove}
      data-testid={`product-card-${p.slug}`}
      className="spotlight clip-frame-sm group relative flex h-[460px] flex-col overflow-hidden border border-gold/20 bg-cellar transition-colors duration-500 hover:border-gold/50 md:h-[560px]"
    >
      <img
        src={p.image}
        alt={p.name}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/20" />
      <span className="absolute start-5 top-5 text-[0.7rem] uppercase tracking-[0.3em] text-parchment/75">
        {p.unit}
      </span>
      <div className="relative mt-auto p-6">
        <p className="text-[0.72rem] uppercase tracking-[0.3em] text-gold/90">{t(`products.categories.${p.cat}`)}</p>
        <h3 className="font-display mt-2 text-2xl text-parchment md:text-3xl">{p.name}</h3>
        {desc && <p className="mt-3 max-w-[36ch] text-[0.95rem] leading-relaxed text-parchment/75 md:text-base">{desc}</p>}
        <div className="mt-5 flex items-center justify-end border-t border-gold/25 pt-4">
          <button
            data-testid={`add-to-request-${p.slug}`}
            onClick={() => {
              add(p);
              openDrawer();
            }}
            className="flex items-center gap-2 border border-gold/60 px-5 py-2.5 text-[0.7rem] uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-obsidian"
          >
            <Plus size={13} />
            {t("products.add")}
          </button>
        </div>
      </div>
    </motion.article>
  );
}

export default function Products() {
  const { t } = useLang();
  const [products, setProducts] = useState(FALLBACK);

  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => Array.isArray(data) && data.length && setProducts(data))
      .catch(() => {});
  }, []);

  return (
    <section
      id="collection"
      data-testid="collection"
      className="relative px-6 py-28 md:px-12 md:py-40"
      style={{ backgroundImage: "url(/parchment.jpg)", backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="pointer-events-none absolute inset-0 bg-[#F7EDD3]/55" />
      <div className="relative mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="text-[0.65rem] uppercase tracking-[0.5em] text-goldink"
          >
            {t("products.eyebrow")}
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.15 }}
            className="font-display text-ink mt-6 text-4xl md:text-6xl"
          >
            {t("products.title")}
          </motion.h2>
        </div>
        <motion.p
          variants={fadeUp}
          custom={2}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="text-xs uppercase tracking-[0.3em] text-ink2"
        >
          {t("products.note")}
        </motion.p>
      </div>

      <motion.div
        variants={{ show: { transition: { staggerChildren: 0.13 } } }}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.1 }}
        className="relative grid gap-8 md:grid-cols-3"
      >
        {products.map((p, i) => (
          <ProductCard key={p.slug} p={p} i={i} />
        ))}
      </motion.div>
    </section>
  );
}
